import { useMultiParentFileUpdate } from '@risksmart-app/components/src/file/useFileUpdate';
import Modal from '@risksmart-app/components/src/modal';
import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import { useGetTestResultById, useGetTestResults } from 'src/hooks/queries';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useInsertControlTestResult } from '@/hooks/mutations/test-result/useInsertControlTestResult';
import { useUpdateTestResult } from '@/hooks/mutations/test-result/useUpdateTestResult';
import { useGetControlById } from '@/hooks/queries/control/useGetControlById';

import ConnectedSecondLineControlTestResultForm from '../../../assessments/oversight-assessments/forms/ConnectedSecondLineControlTestResultForm';
import type { AssessmentTypeEnum } from '../../../assessments/types';
import ConnectedInternalAuditControlTestResultForm from '../../../internal-audit/reports/forms/ConnectedInternalAuditControlTestResultForm';
import TestResultForm from '../forms/TestResultForm';
import type { TestResultFormFieldsData } from '../forms/testResultSchema';
import { defaultValues } from '../forms/testResultSchema';
import { buildInsertControlTestResultArgs } from './buildInsertControlTestResultArgs';

type Props = {
  onDismiss: (saved?: boolean) => void;
  parentControlId: string;
  Id?: string;
  assessmentMode: AssessmentTypeEnum;
};

const StandardTestResultModal: FC<Props> = ({
  onDismiss,
  parentControlId,
  Id,
  assessmentMode,
}) => {
  const { updateFiles } = useMultiParentFileUpdate();
  const { data: controlData, error } = useGetControlById({
    queryArgs: { controlId: parentControlId },
  });
  if (error) {
    throw error;
  }
  const control = controlData?.control[0];
  const { insertControlTestResult } = useInsertControlTestResult();
  const { updateTestResult: update } = useUpdateTestResult();

  const { data, loading, refetch } = useGetTestResultById({
    queryArgs: { testResultId: Id ?? '' },
    shouldSkip: !Id,
  });
  const { refetch: refetchTestResults } = useGetTestResults({ queryArgs: {} });

  const testResult = data?.test_result[0];
  const {
    hasPermission: canEditTestResult,
    loading: canEditTestResultLoading,
  } = useHasPermissionQuery('update:test_result', control);
  const {
    hasPermission: canCreateTestResult,
    loading: canCreateTestResultLoading,
  } = useHasPermissionQuery('insert:test_result', control);

  const canModify = testResult
    ? canEditTestResult && !canEditTestResultLoading
    : canCreateTestResult && !canCreateTestResultLoading;

  const onSave = async (data: TestResultFormFieldsData) => {
    const { files } = data;
    let parentIds: string[];
    if (testResult) {
      const { ParentControlIds: _, files: __, ...rest } = data;
      const result = await update({
        ...rest,
        OriginalTimestamp: testResult.ModifiedAtTimestamp,
        Id: testResult.Id,
        ParentControlId: testResult.ParentControlId,
        CustomAttributeData: rest.CustomAttributeData || undefined,
        Submitter: data.Submitter.value,
      });
      if (!result.updateTestResultApi?.Id) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
      parentIds = [testResult.Id];
      refetch();
    } else {
      const result = await insertControlTestResult(
        buildInsertControlTestResultArgs(data)
      );
      parentIds = result.insertControlTestResult?.Ids ?? [];
      if (parentIds.length === 0) {
        throw new Error('Id not found');
      }
    }

    await updateFiles({
      parentType: Parent_Type_Enum.TestResult,
      parentIds,
      originalFiles: testResult?.files.map((f) => f.file),
      selectedFiles: files,
    });

    refetchTestResults();
  };

  if (loading) {
    return null;
  }

  return (
    <TestResultForm
      values={
        testResult
          ? {
              ...testResult,
              TestType: toTestType(testResult.TestType),
              files: testResult.files.map((rf) => rf.file),
              ParentControlIds: [{ value: parentControlId }],
              Submitter: { value: testResult.Submitter, type: 'user' },
            }
          : undefined
      }
      defaultValues={{
        ...defaultValues,
        ParentControlIds: [{ value: parentControlId }],
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      readOnly={!canModify}
      disableControlSelect={Id !== undefined}
      renderTemplate={(renderProps) => (
        <ModalWrapper
          testId={'testResultModal'}
          {...renderProps}
          visible={true}
        />
      )}
      assessmentMode={assessmentMode}
    />
  );
};

const TestResultModal: FC<Props> = (props) => {
  if (props.assessmentMode === 'internal_audit_report') {
    return (
      <Modal
        data-testid={'testResultModal'}
        disableContentPaddings={true}
        visible={true}
        onDismiss={(event) => {
          if (event.detail.reason === 'overlay') {
            return;
          }
          props.onDismiss(false);
        }}
      >
        <ConnectedInternalAuditControlTestResultForm
          readonly={!!props.Id}
          isModalForm={true}
          navigateToResults={false}
          parentId={''}
          controlIds={[props.parentControlId]}
          id={props.Id}
          onDismiss={props.onDismiss}
        />
      </Modal>
    );
  }

  if (props.assessmentMode === 'compliance_monitoring_assessment') {
    return (
      <Modal
        data-testid={'testResultModal'}
        disableContentPaddings={true}
        visible={true}
        onDismiss={(event) => {
          if (event.detail.reason === 'overlay') {
            return;
          }
          props.onDismiss(false);
        }}
      >
        <ConnectedSecondLineControlTestResultForm
          readonly={!!props.Id}
          isModalForm={true}
          navigateToResults={false}
          parentId={''}
          controlIds={[props.parentControlId]}
          id={props.Id}
          onDismiss={props.onDismiss}
        />
      </Modal>
    );
  }

  return <StandardTestResultModal {...props} />;
};

export default TestResultModal;
